function isValidPresignedUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname.endsWith(".blob.core.windows.net");
  } catch {
    return false;
  }
}

/**
 * Raw XHR PUT to an Azure Blob Storage SAS URL (see DocuParserOptions /
 * DocuParserBlobStorage on the backend) — auth is carried in the URL's query
 * string, so this deliberately bypasses serviceFetch (no adapter/bearer
 * header applies here). Azure's Put Blob REST API requires `x-ms-blob-type`
 * on every PUT — without it the request 400s with MissingRequiredHeader,
 * unlike an S3 presigned PUT which needs no extra headers at all.
 */
export function uploadToPresignedUrl(
  presignedUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
  abortSignal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!isValidPresignedUrl(presignedUrl)) {
      reject(new Error("Invalid presigned URL: URL must be HTTPS and from a blob.core.windows.net domain"));
      return;
    }

    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Blob upload failed with status: ${xhr.status}`));
      }
    });
    xhr.addEventListener("error", () => reject(new Error("Network error during blob upload")));
    xhr.addEventListener("abort", () => reject(new Error("Upload cancelled")));

    if (abortSignal) {
      abortSignal.addEventListener("abort", () => xhr.abort());
    }

    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.send(file);
  });
}

/** Fetches a presigned GET URL and returns its content as a base64 string (no data: prefix), for Word.insertFileFromBase64. */
export function fetchFileAsBase64(url: string): Promise<string> {
  return fetch(url, { method: "GET", mode: "cors" })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }
      return response.blob();
    })
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            resolve(result.split(",")[1] ?? "");
          };
          reader.onerror = () => reject(new Error("FileReader failed to read blob"));
          reader.readAsDataURL(blob);
        }),
    );
}
